import { useEffect, useRef, useCallback, useState } from "react";

/**
 * useWorldMusic — plays Harry Potter themed background music.
 *
 * Track: "Hogwarts Express (Inspired by Harry Potter)" by Luis Humanoide
 * Source: Pixabay (royalty-free, no attribution required)
 */

export function useWorldMusic() {
  const [muted, setMuted] = useState(true); // Start muted, user opts in
  const [currentWorld] = useState("harryPotter");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Play or pause based on muted state
  useEffect(() => {
    if (muted) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      return;
    }

    // Create audio element on first unmute
    if (!audioRef.current) {
      const audio = new Audio("/audio/hp-theme.mp3");
      audio.loop = true;
      audio.volume = 0;
      audioRef.current = audio;
    }

    const audio = audioRef.current;
    audio.volume = 0;
    audio.play().catch(() => {});

    // Fade in
    const targetVol = 0.12;
    const fadeIn = setInterval(() => {
      if (audio.volume < targetVol - 0.01) {
        audio.volume = Math.min(targetVol, audio.volume + 0.01);
      } else {
        audio.volume = targetVol;
        clearInterval(fadeIn);
      }
    }, 30);

    return () => clearInterval(fadeIn);
  }, [muted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(m => !m);
  }, []);

  return { muted, toggleMute, currentWorld };
}
